import { Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatIconModule } from "@angular/material/icon";
import { BadgeComponent, EnrTooltipDirective } from "@corp-ais/omniark-ui";
import {
  channelNames,
  EnrichmentDetail,
} from "@app/shared/models/enrichment-detail.model";
import { isFlatEnrichmentModel } from "@app/shared/mappers/enrichment-detail.adapter";
import { isRejectableStatus } from "@app/shared/models/enrichment-list.model";
import {
  PACKAGE_STATUS_HINT,
  PACKAGE_TONE_CLASS,
  PackageStatusView,
  packageStatusView,
} from "@app/shared/models/package-status.model";
import { textOf } from "@shared/utils/text-like.util";
import { offerClassificationLabel } from "../../../mappers/poc.util";
import { EnrichmentDataService } from "../../../services/enrichment-data.service";
import { ActivePriceStore } from "../enrichment-edit-package/active-price.store";
import {
  describeValidFor,
  VALID_FOR_BADGE_COLOR,
  ValidForView,
} from "../enrichment-pricing/valid-for.util";
import { EnrichmentTabProductAttributes } from "../enrichment-tab/enrichment-tab-product-attributes/enrichment-tab-product-attributes";
import { EnrichmentTerm } from "../enrichment-term/enrichment-term";

export interface PackageFact {
  readonly label: string;
  readonly value: string;
  /** Where a readiness deep link for this fact lands. */
  readonly readinessTarget?: string;
}

export interface PackageSummary {
  readonly name: string;
  /** From the package's own lifecycleStatus only, so the editor and the list
   *  never disagree. */
  readonly status: PackageStatusView | undefined;
  readonly version: string;
  readonly validity: ValidForView | undefined;
  readonly channels: string[];
  readonly facts: PackageFact[];
}

const dash = (value: string | undefined | null): string =>
  value?.trim() ? value : "—";

export function packageSummary(
  detail: EnrichmentDetail,
  now: Date,
): PackageSummary {
  return {
    name: textOf(detail.name).trim() || "Untitled package",
    status: packageStatusView(detail.lifecycleStatus),
    version: detail.version ?? "",
    validity: describeValidFor(detail.validFor, now),
    channels: detail.salesChannels?.length
      ? detail.salesChannels
      : channelNames(detail),
    facts: [
      { label: "Product line", value: dash(detail.productLine) },
      { label: "Product type", value: dash(detail.productType) },
      {
        label: "Offer classification",
        value: dash(offerClassificationLabel(detail.offerClassificationName)),
        readinessTarget: "offer-classification",
      },
      { label: "Product group", value: dash(detail.productGroup?.[0]) },
    ],
  };
}

@Component({
  selector: "app-enrichment-package-settings",
  imports: [
    BadgeComponent,
    EnrTooltipDirective,
    MatIconModule,
    EnrichmentTerm,
    EnrichmentTabProductAttributes,
  ],
  templateUrl: "./enrichment-package-settings.html",
})
export class EnrichmentPackageSettings {
  private readonly active = inject(ActivePriceStore);
  private readonly data = inject(EnrichmentDataService);

  private readonly model = toSignal(this.data.enrichment$, {
    initialValue: null,
  });

  protected readonly priceCount = computed(() => this.active.entries().length);

  protected readonly liveCount = computed(
    () =>
      this.active
        .entries()
        .filter((entry) => isRejectableStatus(entry.status?.type)).length,
  );

  protected readonly pricesLabel = computed(() => {
    const count = this.priceCount();
    if (count === 0) return "No prices";
    const prices = `${count} ${count === 1 ? "price" : "prices"}`;
    const live = this.liveCount();
    return live > 0 ? `${prices} · ${live} live` : prices;
  });

  protected readonly summary = computed<PackageSummary | null>(() => {
    const model = this.model();
    return model && isFlatEnrichmentModel(model)
      ? packageSummary(model.__detail, new Date())
      : null;
  });

  protected readonly toneClass = PACKAGE_TONE_CLASS;
  protected readonly statusHint = PACKAGE_STATUS_HINT;
  protected readonly validityBadgeColor = VALID_FOR_BADGE_COLOR;
}
